import { useEffect, useState } from "react";
import { DynamicForm, SimpleModal, UpsertDraftOrderBusinessFormBody } from "../../components";
import {
    UpsertDraftOrderDTO,
    UpsertDraftOrdersDTO,
    UpsertDraftOrderBusinessDTOSchema,
    UpsertDraftOrdersBusinessDTOSchema,
    DraftWritingType,
} from "src/app/types/orders/order";
import { CheckRoles } from "src/app/utils/CheckRoles";
import { SimpleFormSelectOption } from "../../components/form/SimpleFormSelect";
import { apiToFormikErrors } from "../../utils/formHelpers";
import { SiteTransport } from "src/app/transports/site";
import { Formik, FormikHelpers } from "formik";
import { ThematicTransport } from "src/app/transports/thematic";
import { OrderTransport } from "src/app/transports/order";
import { toast } from "react-toastify";
import { useIntl } from "react-intl";
import { useAuth } from "src/app/utils/useAuth";
import { PlatformTransport } from "src/app/transports/platform";
import { Platform } from "src/app/types/Platform";
import { UserModel } from "src/app/types/UserModel";

const FORMID = "DynamicOrderForm";
export interface IDataUpsertOrderDTO {
    data: Array<UpsertDraftOrderDTO | any>;
}

export interface IOrderFormModalContainer {
    handleClose?(): void;
    isModalOpen?: boolean;
    userSelected?: UserModel;
}

const DraftOrderBusinessFormModalContainer = (props: IOrderFormModalContainer) => {
    const { handleClose = () => {}, isModalOpen = false, userSelected } = props;
    const [writingTypeList, setWritingTypeList] = useState<Array<SimpleFormSelectOption>>([]);
    const [moneySiteList, setMoneySiteList] = useState<Array<SimpleFormSelectOption>>([]);
    const [platformList, setPlatformList] = useState<Array<SimpleFormSelectOption>>([]);
    const [thematicList, setThematicList] = useState<Array<SimpleFormSelectOption>>([]);
    const { currentUser } = useAuth();
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState<IDataUpsertOrderDTO>({
        data: [UpsertDraftOrderBusinessDTOSchema.getDefault()],
    });
    const { formatMessage } = useIntl();

    const getFormDataFromDB = async () => {
        const orders = await OrderTransport.listDraft(userSelected.id || currentUser.id);
        let formDraftTransformer: UpsertDraftOrdersDTO | { data: Array<any> } = {
            data: [UpsertDraftOrderBusinessDTOSchema.getDefault()],
        };

        if (orders?.length > 0) formDraftTransformer = OrderTransport.formDraftTransformer(orders);
        setFormData(formDraftTransformer);
        return formDraftTransformer;
    };

    const onSubmit = async (_values: UpsertDraftOrdersDTO, formikHelpers: FormikHelpers<any>) => {
        setLoading && setLoading(true);
        const currentData = _values?.data?.map((value) => ({
            ...value,
            draft: !value.draft,
        }));
        try {
            await OrderTransport.saveDraft(
                { data: currentData },
                userSelected.id || currentUser.id
            );
            toast.success(formatMessage({ id: "common.notification.success" }));
            formikHelpers.setValues(await getFormDataFromDB());
        } catch (error: any) {
            toast.error(formatMessage({ id: "common.notification.error" }));
            apiToFormikErrors(error?.response, formikHelpers);
        } finally {
            setLoading && setLoading(false);
        }
        setLoading && setLoading(false);
    };

    useEffect(() => {
        (async () => {
            setLoading && setLoading(true);
            await getFormDataFromDB();

            setMoneySiteList(
                await SiteTransport.optionsByProjectManager(userSelected.id || currentUser.id)
            );

            setThematicList(await ThematicTransport.allOptions({ isActive: 1 }));

            if (CheckRoles.isLeadBussinessOrUnder(currentUser.role)) {
                const platforms = await PlatformTransport.listByProjectManager(
                    userSelected.id || currentUser.id
                );
                setPlatformList(
                    platforms.map((platform: Platform) => {
                        return { value: platform.id, label: platform.name };
                    })
                );
            }

            const writingTypeListBuilder: Array<SimpleFormSelectOption> = [];

            for (const key in DraftWritingType) {
                writingTypeListBuilder.push({
                    value: DraftWritingType[key as keyof typeof DraftWritingType],
                    label: formatMessage({
                        id: `content.grid.col.writingType.${
                            DraftWritingType[key as keyof typeof DraftWritingType]
                        }`,
                    }),
                });
            }

            setLoading && setLoading(false);
            setWritingTypeList(writingTypeListBuilder);
        })();
    }, []);

    return (
        <>
            <Formik
                enableReinitialize={true}
                initialValues={formData}
                validationSchema={UpsertDraftOrdersBusinessDTOSchema}
                onSubmit={onSubmit}
            >
                {(formikProps) => {
                    return (
                        <SimpleModal
                            showModal={isModalOpen}
                            loading={loading}
                            entityName={
                                userSelected.id === currentUser.id
                                    ? formatMessage({ id: "common.entity.draftOrderBusiness" })
                                    : `${userSelected.firstName} ${
                                          userSelected.lastName
                                      } ${formatMessage({
                                          id: "dashboard.button.draftOrderBusiness",
                                      })}`
                            }
                            buttonConfirmationLabel={formatMessage({
                                id: "common.action.saveDraft",
                            })}
                            formId={FORMID}
                            modalProps={{ fullscreen: true }}
                            handleClose={handleClose}
                        >
                            <>
                                {!loading && (
                                    <DynamicForm
                                        formId={FORMID}
                                        singleInicialValues={UpsertDraftOrderBusinessDTOSchema.getDefault()}
                                        formData={formData}
                                        setFormData={setFormData}
                                        formikProps={formikProps}
                                    >
                                        <UpsertDraftOrderBusinessFormBody
                                            moneySiteList={moneySiteList}
                                            writingTypeList={writingTypeList}
                                            platformList={platformList}
                                            thematicList={thematicList}
                                        />
                                    </DynamicForm>
                                )}
                            </>
                        </SimpleModal>
                    );
                }}
            </Formik>
        </>
    );
};

export default DraftOrderBusinessFormModalContainer;
